import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit, OnDestroy, Input, inject } from "@angular/core";
import { IonicModule, Platform } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription, interval } from "rxjs";
import { CallModalButton, DoorBellText } from "src/app/interfaces/interfaces";
import { CommunicationServiceService } from "src/app/services/azure-communication-service/communication-service.service";
import { CameraPreview } from "@capgo/camera-preview";
import { trigger, style, animate, transition } from "@angular/animations";
import { AudioService } from "src/app/services/Audio/audio.service";
import { LedControllerService } from "src/app/services/LedController/led-controller.service";
import { NetworkserviceService, ConnStatus } from "src/app/services/Networkservice/networkservice.service";
import { addIcons } from "ionicons";
import { call, micOutline, close } from "ionicons/icons";
import { Volumes } from "@ottimis/capacitor-volumes";
import { ModalStateService } from "src/app/services/modal-service/modal-state.service";

@Component({
	selector: "app-call-modal",
	templateUrl: "./call-modal.component.html",
	styleUrls: ["./call-modal.component.scss"],
	animations: [
		trigger("iconChange", [
			transition(":enter", [
				style({ opacity: 0, transform: "scale(0.8)" }),
				animate("100ms ease-out", style({ opacity: 1, transform: "scale(1)" })),
			]),
		]),
	],
	imports: [IonicModule, TranslateModule, CommonModule],
})
export class CallModalComponent implements OnInit, OnDestroy {
	@Input() button: CallModalButton;

	callDuration: string = "00:00";
	callStatus: string = "Connecting...";
	callEnded: boolean = false;
	isConnected: boolean = false;
	callWasConnected: boolean = false;
	callFailed: boolean = false;
	callRejected: boolean = false;
	timerInterval;

	private callStartTime!: number;
	private timerSubscription: Subscription | null = null;
	private callStateSubscription: Subscription | null = null;

	private timeouts: NodeJS.Timeout[] = [];

	private isDE: boolean;

	platform = inject(Platform);

	private hangUpAction = this.endCall.bind(this, "END_BY_USER");
	modalState: ModalStateService;

	private networkService = inject(NetworkserviceService);

	constructor(
		private communicationService: CommunicationServiceService,
		private translate: TranslateService,
		private ledControllerService: LedControllerService,
		private audioService: AudioService,
		private changeDetector: ChangeDetectorRef
	) {
		this.modalState = inject(ModalStateService);
		addIcons({ micOutline, call, close });
	}

	ngOnInit(): void {
		const language = this.translate.currentLang;
		this.isDE = language === "de";
		this.modalState.setShowFooterButton(false);
		// triggerAutomation is passed via dashboard Input
		this.button.triggerAutomation();
		this.startCallProcess();
	}

	ngOnDestroy(): void {
		this.ledControllerService.setLedWhite();
		this.audioService.stop();

		this.cleanupResources();
	}

	async startCallProcess(): Promise<void> {
		this.ledControllerService.setLedGreen();
		try {
			this.communicationService.resetCallState();
			this.audioService.play("CallRinging");
			this.timerInterval = setInterval(() => {
				if (this.audioService.checkIfAudioEnded()) {
					this.audioService.play("CallRinging");
				}
			}, 3000);
			this.callStatus = this.translate.instant("CALL.WAITING");
			this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_CONNECTING");
			this.startUnansweredTimeout();

			const isInitialized = await this.ensureCommunicationServiceInitialized();
			if (!isInitialized) {
				this.onCallFailed();
				return;
			}

			await this.initiateCall();
			this.subscribeToCallState();
		} catch (error) {
			this.onCallFailed();
		}
	}

	private async ensureCommunicationServiceInitialized(): Promise<boolean> {
		if (this.communicationService.isInitialized) {
			return true;
		}
		try {
			return await this.communicationService.initialize(this.button.callConnector);
		} catch (error) {
			return false;
		}
	}

	private async initiateCall(): Promise<void> {
		//only possible for android to set the volume
		if (this.platform.is("android")) {
			Volumes.setVolumeLevel({ value: 10 });
		}
		await this.communicationService.startCall(
			this.button.recipientId,
			this.button.callType === "USER" ? "User" : "Resource"
		);
	}
	private subscribeToCallState(): void {
		this.callStateSubscription = this.communicationService.callState$.subscribe((state) => {
			if (state === "Connected") {
				this.onCallConnected();
			} else if (!this.callEnded && state === "Disconnected") {
				if (this.callWasConnected) {
					this.endCall();
				} else {
					this.onCallFailed();
				}
			} else if (state === "Declined") {
				this.endCall("END_BY_CALLEE");
			} else if (state === "Error") {
				this.onCallFailed();
			}
		});
	}
	private onCallConnected(): void {
		if (this.callEnded || this.isConnected) {
			return;
		}
		clearInterval(this.timerInterval);
		this.callWasConnected = true;
		this.modalState.setShowFooter(true);
		this.modalState.setShowFooterButton(true);
		this.modalState.setFooterButtonText("CALL.ENDBUTTON");
		this.modalState.setFooterButtonColor("red");
		this.modalState.setFooterAction(this.hangUpAction);
		this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_PROCESSING");
		this.audioService.stop();
		this.clearUnansweredTimeout();
		this.isConnected = true;
		this.callStatus = this.translate.instant("CALL.CONNECTED");
		this.startCallTimer();
		this.startAutoEndTimeout();
	}

	private onCallFailed(): void {
		this.endCall("END_BY_FAILED");
	}

	private startUnansweredTimeout(): void {
		this.addTimeout(
			setTimeout(() => {
				if (!this.isConnected) {
					this.onCallFailed();
				}
			}, 30000)
		);
	}

	private clearUnansweredTimeout(): void {
		this.clearAllTimeouts();
	}

	private startAutoEndTimeout(): void {
		this.addTimeout(
			setTimeout(() => {
				this.endCall("END_BY_AUTO");
			}, 5 * 60 * 1000)
		);
	}

	private startCallTimer(): void {
		this.callStartTime = Date.now();
		this.timerSubscription = interval(1000).subscribe(() => {
			const elapsedSeconds = Math.floor((Date.now() - this.callStartTime) / 1000);
			this.callDuration = this.formatTime(elapsedSeconds);
			this.changeDetector.detectChanges();
		});
	}

	private formatTime(totalSeconds: number): string {
		const minutes = Math.floor(totalSeconds / 60)
			.toString()
			.padStart(2, "0");
		const seconds = (totalSeconds % 60).toString().padStart(2, "0");
		return `${minutes}:${seconds}`;
	}

	private addTimeout(timeout: NodeJS.Timeout): void {
		this.timeouts.push(timeout);
	}

	private clearAllTimeouts(): void {
		clearInterval(this.timerInterval);
		this.timeouts.forEach((timeout) => clearTimeout(timeout));
		this.timeouts = [];
	}

	private cleanupResources(): void {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
		if (this.callStateSubscription) {
			this.callStateSubscription.unsubscribe();
			this.callStateSubscription = null;
		}
		this.clearAllTimeouts();
		this.communicationService.endCall();
	}

	closeModal(): void {
		this.ledControllerService.setLedWhite();
		this.audioService.stop();
		const role = this.callRejected || this.callFailed ? "leave-message" : "cancel";
		this.modalState.triggerDismiss(role);
	}

	endCall(endCallReason?: string): void {
		if (this.callEnded) {
			return;
		}
		this.callEnded = true;
		this.isConnected = false;
		this.ledControllerService.applyRedBlinkingEffect();
		this.audioService.stop();
		this.audioService.play("CallEnd");
		this.updateCallStatus(endCallReason);
		this.modalState.setShowFooter(false);
		this.modalState.setShowFooterButton(false);
		this.stopCamera();
		this.cleanupResources();
		this.changeDetector.detectChanges();
		this.addTimeout(setTimeout(() => this.closeModal(), 15000));
	}
	async stopCamera(): Promise<void> {
		await CameraPreview.stop().then(() => {});
	}

	private setLang(msg: DoorBellText): string {
		return this.isDE ? msg.de : msg.en;
	}

	private updateCallStatus(endCallReason?: string): void {
		if (!endCallReason) {
			this.callStatus = this.translate.instant("CALL.ENDED");
			this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_FINISHED");
			return;
		}

		switch (endCallReason) {
			case "END_BY_USER":
				this.callStatus = this.translate.instant("CALL.ENDED");
				this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_FINISHED");
				this.audioService.scheduleAudioStop(2000);
				break;
			case "END_BY_CALLEE":
				this.callStatus = this.setLang(this.button.rejectedCallingMessage);
				this.callRejected = true;
				this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_REJECTED");
				break;
			case "END_BY_FAILED":
				this.callFailed = true;
				this.callStatus = this.translate.instant(
					this.networkService.status.value === ConnStatus.Offline
						? "CALL.FAILED_NO_NETWORK"
						: "CALL.FAILED"
				);
				this.updateStatusAndEmitHeader("CALL.FAILED_HEADER");
				break;
			case "END_BY_AUTO":
				this.callStatus = this.setLang(this.button.endCallingMessage);
				this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_FINISHED");
				break;
			default:
				this.callStatus = this.translate.instant("CALL.ENDED");
				this.updateStatusAndEmitHeader("CALL.HEADER_STATUS_FINISHED");
				break;
		}
	}

	private updateStatusAndEmitHeader(statusKey: string): void {
		const interpolateParams = { name: this.button.buttonName || "Unknown" };

		const translatedStatus = this.translate.instant(statusKey, interpolateParams);
		this.modalState.setHeader(translatedStatus);
	}
}
